/**
 * stages.ts — the 12 master call stages + the six-zone display grouping.
 *
 * Source of truth: 03_stage-by-stage-advisor-guide.md (stage names, purposes,
 * completion outputs) and 24_live-ai-coaching-prompts.md §A (detection cues).
 * The six-zone strip is a DISPLAY GROUPING over the 12 stages (GUI v2 req A.1);
 * the grouping judgment calls are documented in docs/LIVE_COACH_GUI2_2026-06-11.md
 * and surfaced to Phil there.
 */
import { STAGE_ZONE, type StageId, type ZoneKey } from "@/types";

export interface StageDef {
  id: StageId;
  name: string;
  /** The output that marks the stage complete (24_live-ai-coaching-prompts.md §A). */
  doneWhen: string;
  zone: ZoneKey;
}

export const STAGES: StageDef[] = [
  { id: 1, name: "Open", doneWhen: "Greeted, named self + shop, customer talking", zone: STAGE_ZONE[1] },
  { id: 2, name: "Capture Contact", doneWhen: "First/last name + phone captured", zone: STAGE_ZONE[2] },
  { id: 3, name: "Vehicle / Scope", doneWhen: "Year, make, model + vehicle class known", zone: STAGE_ZONE[3] },
  { id: 4, name: "Product Path", doneWhen: "Tint, PPF, coating, bundle, or unsure — set", zone: STAGE_ZONE[4] },
  { id: 5, name: "Discover Motivation", doneWhen: "Why-now + primary motivation in their words", zone: STAGE_ZONE[5] },
  { id: 6, name: "Confirm Problem", doneWhen: "Gap restated, customer said yes", zone: STAGE_ZONE[6] },
  { id: 7, name: "Recommend", doneWhen: "Education moment + one specific recommendation", zone: STAGE_ZONE[7] },
  { id: 8, name: "Build Value", doneWhen: "Value tied to their reason, warranty named", zone: STAGE_ZONE[8] },
  { id: 9, name: "Present Investment", doneWhen: "Exact price stated plainly", zone: STAGE_ZONE[9] },
  { id: 10, name: "Handle Concerns", doneWhen: "Acknowledge, clarify, educate, reconnect, ask", zone: STAGE_ZONE[10] },
  { id: 11, name: "Close", doneWhen: "Best available outcome secured", zone: STAGE_ZONE[11] },
  { id: 12, name: "Confirm + Follow-Up", doneWhen: "Read-back done, CRM complete, follow-up dated", zone: STAGE_ZONE[12] },
];

export interface ZoneDef {
  key: ZoneKey;
  /** Short label for the strip (GUI v2 req A.1 vocabulary). */
  label: string;
  stages: StageId[];
}

export const ZONES: ZoneDef[] = [
  { key: "open", label: "Open", stages: [1, 2] },
  { key: "discover", label: "Discover", stages: [3, 4, 5, 6] },
  { key: "build_value", label: "Build Value", stages: [7, 8] },
  { key: "present_price", label: "Present + Price", stages: [9] },
  { key: "objections", label: "Objections", stages: [10] },
  { key: "close_book", label: "Close + Book", stages: [11, 12] },
];

export function zoneIndex(zone: ZoneKey): number {
  return ZONES.findIndex((z) => z.key === zone);
}

export function zoneForStage(stageId: StageId): ZoneKey {
  return STAGE_ZONE[stageId];
}
